import { useState } from 'react';

import { ArrowRightLeft, ArrowUpDown, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import {
  InconsistencyLevel,
  InconsistencySource,
  InconsistencyType,
} from '../../../../../../../../backend/src/case/inconsistency/inconsistency-display';
import DocComparer from '../../../DataView/InconsistenciesEditor/components/DocComparer';
import {
  getFactGroupColors,
  getInconsistencyLevelConfig,
  getInconsistencySourceConfig,
  getInconsistencyTypeConfig,
} from '../../../DataView/InconsistenciesEditor/components/InconsistencyComponents';
import { EvidenceCountIndicator } from '../../../DataView/InconsistenciesEditor/components/InconsistencyIndicators';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { BackButton } from '@/components/atoms/BackButton';
import { Button } from '@/components/atoms/new-button';
import { TextWithReferences } from '@/helpers/formatReferences';
import { Inconsistency } from '@/types';

interface InconsistencyFullViewProps {
  inconsistency: Inconsistency;
  caseId?: string;
  onBack: () => void;
}

const InconsistencyFullView = ({ inconsistency, caseId, onBack }: InconsistencyFullViewProps) => {
  const [docComparerOpen, setDocComparerOpen] = useState(false);
  const navigate = useNavigate();

  const renderFactGroups = () => {
    const docGroups = inconsistency.facts ?? {};
    const groupNames = Object.keys(docGroups);
    const colors = getFactGroupColors();

    return groupNames.map((groupName: string, index: number) => (
      <div key={groupName} className="mb-3">
        <div className={`relative rounded-md ${colors[index % colors.length].bg} p-3`}>
          <div
            className={`absolute top-1/2 -left-2 h-full w-1 -translate-y-1/2 rounded-full ${colors[index % colors.length].line}`}
          />
          <h3 className="mb-2 text-xs font-semibold text-gray-900">{groupName}</h3>
          <div className="space-y-2">
            {inconsistency.facts[groupName].map((fact: any) => (
              <div key={fact.id} className="rounded-md border border-gray-200 bg-white p-2 shadow-sm">
                <div className="mb-1 flex w-full justify-between">
                  <div className="flex items-center gap-2 text-xs text-blue-600">
                    {fact.fact?.locations && fact.fact.locations.length > 0 && (
                      <span className="text-xs text-gray-500">
                        {fact.fact.locations[0]?.doc?.file?.name?.slice(0, 15)}...
                      </span>
                    )}
                  </div>
                  {fact.fact?.factDateText && <div className="text-xs text-gray-500">{fact.fact.factDateText}</div>}
                </div>
                <p className="text-xs leading-relaxed text-gray-800">{fact.fact?.description}</p>
              </div>
            ))}
          </div>
        </div>
        {index < groupNames.length - 1 && (
          <div className="my-3 flex w-full items-center justify-center">
            <div className="flex items-center justify-center rounded-full border bg-gray-100 p-1">
              <ArrowUpDown className="h-3 w-3 text-gray-600" />
            </div>
          </div>
        )}
      </div>
    ));
  };

  return (
    <>
      <div className="flex flex-col leading-relaxed">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between border-b border-gray-100 pb-4">
          <div className="flex items-center gap-4">
            <BackButton onClick={onBack} />
            <h1 className="text-lg font-semibold text-gray-900">Inconsistency Details</h1>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="text-semibold flex items-center gap-2 shadow"
            onClick={() => {
              navigate(`/app/chronos/case-editor/data/inconsistencies?caseId=${caseId}`);
            }}
          >
            View All Inconsistencies <ArrowRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Full Content */}
        <div className="rounded-lg bg-white p-6 shadow-sm">
          {/* Header with badges */}
          <div>
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                {/* Level Badge */}
                {(() => {
                  const levelConfig = getInconsistencyLevelConfig(inconsistency.level as InconsistencyLevel);
                  const Icon = levelConfig.Icon;
                  return (
                    <div
                      className={`flex items-center gap-1 rounded-md px-2 py-0.5 transition-colors ${levelConfig.className}`}
                    >
                      <Icon className="h-3 w-3" />
                      <span className="text-xs font-semibold">{levelConfig.label}</span>
                    </div>
                  );
                })()}

                {/* Source Badge */}
                {(() => {
                  const sourceConfig = getInconsistencySourceConfig(inconsistency.source as InconsistencySource);
                  return (
                    <div className={`flex rounded-md px-2 py-0.5 ${sourceConfig.className}`}>
                      <span className="text-xs font-semibold">{sourceConfig.label}</span>
                    </div>
                  );
                })()}

                {/* Type Badge */}
                {(() => {
                  const typeConfig = getInconsistencyTypeConfig(inconsistency.type as InconsistencyType);
                  const Icon = typeConfig.Icon;
                  return (
                    <div
                      className={`flex items-center gap-1 rounded-md px-2 py-0.5 transition-colors ${typeConfig.className}`}
                    >
                      <Icon className="h-3 w-3" />
                      <span className="text-xs font-semibold">{typeConfig.label}</span>
                    </div>
                  );
                })()}
                <EvidenceCountIndicator
                  count={Object.values(inconsistency.facts ?? {}).flat().length}
                  className="flex py-0.5 text-xs"
                />
              </div>
            </div>

            {/* Description */}
            <div className="mb-4">
              <h3 className="mb-2 text-xs font-medium text-gray-700">Description</h3>
              <div className="rounded-lg bg-gray-50 p-3 text-xs leading-relaxed text-gray-800">
                <TextWithReferences content={inconsistency.description} />
              </div>
            </div>
          </div>

          {/* Related Facts */}
          <div>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-xs font-medium text-gray-700">Contradictory Evidence</h3>
              {Object.keys(inconsistency.facts).length >= 2 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-semibold flex items-center gap-2 shadow"
                  onClick={() => {
                    setDocComparerOpen(true);
                    trackCustomEvent(TrackingEvents.OpenInconsistencyDocComparer);
                  }}
                >
                  Compare Docs
                  <ArrowRightLeft className="mr-2 h-4 w-4" />
                </Button>
              )}
            </div>
            <div className="max-h-96 space-y-3 overflow-y-auto">{renderFactGroups()}</div>
          </div>
        </div>
      </div>

      {/* Doc Comparer Modal */}
      {docComparerOpen && <DocComparer docGroups={inconsistency.facts} caseId={caseId ?? ''} />}
    </>
  );
};

export default InconsistencyFullView;
